import React, { useState, useEffect } from "react";
import axios from "axios";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ArrowUpRight, SignOut, Trash, Envelope, Phone, CalendarBlank, UsersThree, House, Clock, Prohibit, Plus, ListChecks } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "vd_admin_token";

const api = axios.create();
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const statusColors = {
  new: { bg: "#A35D43", label: "E re" },
  read: { bg: "#5C5C5C", label: "E lexuar" },
  confirmed: { bg: "#1E382C", label: "E konfirmuar" },
  cancelled: { bg: "#7A1F1F", label: "E anuluar" },
};

function toYMD(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("admin");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Kredenciale të pavlefshme");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--color-bg)" }} data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <div className="font-display text-4xl tracking-tight" style={{ color: "var(--color-primary)" }}>
            Villa<span style={{ color: "var(--color-secondary)" }}>·</span>Delight
          </div>
          <div className="overline mt-3">Admin Panel</div>
        </div>
        <form onSubmit={submit} className="space-y-5 p-8" style={{ background: "#fff", border: "1px solid var(--color-border)" }}>
          <div>
            <label className="overline block mb-2">Email / Username</label>
            <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-transparent border-0 border-b-2 focus:outline-none py-3 text-lg" style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }} data-testid="admin-email-input" />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-transparent border-0 border-b-2 focus:outline-none py-3 text-lg" style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }} data-testid="admin-password-input" />
          </div>
          {error && <div className="text-sm py-2 px-3" style={{ background: "#FCE8E8", color: "#7A1F1F" }} data-testid="admin-login-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="admin-login-btn">
            {loading ? "Duke hyrë..." : "Hyr në Admin"}
            {!loading && <ArrowUpRight size={16} />}
          </button>
        </form>
        <a href="/" className="block text-center mt-6 text-sm" style={{ color: "var(--color-muted)" }} data-testid="admin-back-home">← Kthehu te website-i</a>
      </div>
    </div>
  );
}

function BookingCard({ booking, onStatusChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const s = statusColors[booking.status] || statusColors.new;
  const d = new Date(booking.created_at);
  const dateStr = d.toLocaleDateString("sq-AL", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = d.toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="border p-6" style={{ background: "#fff", borderColor: "var(--color-border)" }} data-testid={`booking-card-${booking.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <h3 className="font-display text-2xl" style={{ color: "var(--color-primary)" }}>{booking.name}</h3>
            <span className="px-2 py-0.5 text-xs font-semibold uppercase tracking-wider" style={{ background: s.bg, color: "#fff" }} data-testid={`booking-status-${booking.id}`}>{s.label}</span>
            <span className="text-xs uppercase tracking-wider px-2 py-0.5" style={{ background: "var(--color-bg)", color: "var(--color-muted)" }}>{booking.language}</span>
          </div>
          <div className="flex items-center gap-1 text-xs" style={{ color: "var(--color-muted)" }}>
            <Clock size={12} /> {dateStr} · {timeStr}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={booking.status} onChange={(e) => onStatusChange(booking.id, e.target.value)} className="text-xs uppercase tracking-wider px-3 py-2 cursor-pointer" style={{ background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-primary)" }} data-testid={`booking-status-select-${booking.id}`}>
            <option value="new">E re</option>
            <option value="read">E lexuar</option>
            <option value="confirmed">E konfirmuar</option>
            <option value="cancelled">E anuluar</option>
          </select>
          <button onClick={() => { if (window.confirm(`Fshi rezervimin nga ${booking.name}?`)) onDelete(booking.id); }} className="p-2 hover:bg-red-50 transition-colors" title="Fshi" data-testid={`booking-delete-${booking.id}`}>
            <Trash size={18} color="#7A1F1F" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5 text-sm">
        <a href={`mailto:${booking.email}`} className="flex items-center gap-2 hover:underline" style={{ color: "var(--color-primary)" }}>
          <Envelope size={16} /> {booking.email}
        </a>
        {booking.phone && <a href={`tel:${booking.phone}`} className="flex items-center gap-2 hover:underline" style={{ color: "var(--color-primary)" }}><Phone size={16} /> {booking.phone}</a>}
        {(booking.check_in || booking.check_out) && <div className="flex items-center gap-2" style={{ color: "var(--color-muted)" }}><CalendarBlank size={16} /> {booking.check_in || "—"} → {booking.check_out || "—"}</div>}
        {booking.guests && <div className="flex items-center gap-2" style={{ color: "var(--color-muted)" }}><UsersThree size={16} /> {booking.guests} mysafirë</div>}
        {booking.room && <div className="flex items-center gap-2 md:col-span-2" style={{ color: "var(--color-secondary)" }}><House size={16} /> {booking.room}</div>}
      </div>
      {booking.message && (
        <div className="mt-4">
          <button onClick={() => setExpanded(!expanded)} className="text-xs uppercase tracking-wider" style={{ color: "var(--color-secondary)" }} data-testid={`booking-toggle-msg-${booking.id}`}>
            {expanded ? "Fshih mesazhin" : "Shiko mesazhin"} ↓
          </button>
          {expanded && <p className="mt-3 text-sm leading-relaxed p-4" style={{ background: "var(--color-bg)", color: "var(--color-text)" }} data-testid={`booking-msg-${booking.id}`}>{booking.message}</p>}
        </div>
      )}
      <div className="mt-5 pt-4 border-t flex gap-2 flex-wrap" style={{ borderColor: "var(--color-border)" }}>
        <a href={`mailto:${booking.email}?subject=Re: Rezervimi juaj — Villa Delight&body=Përshëndetje ${booking.name},%0D%0A%0D%0AFaleminderit për rezervimin tuaj...`} className="btn-secondary" style={{ padding: "0.5rem 1rem", fontSize: "0.7rem" }} data-testid={`booking-reply-email-${booking.id}`}>
          <Envelope size={14} /> Përgjigju Email
        </a>
        {booking.phone && <a href={`https://wa.me/${booking.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ padding: "0.5rem 1rem", fontSize: "0.7rem" }} data-testid={`booking-whatsapp-${booking.id}`}><Phone size={14} /> WhatsApp</a>}
      </div>
    </div>
  );
}

function BookingsTab({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  const fetchBookings = async () => {
    try {
      const { data } = await api.get(`${API}/admin/bookings`);
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError("Gabim gjatë ngarkimit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`${API}/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch { setError("Gabim gjatë përditësimit"); }
  };

  const deleteBooking = async (id) => {
    try {
      await api.delete(`${API}/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
    } catch { setError("Gabim gjatë fshirjes"); }
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = {
    all: bookings.length,
    new: bookings.filter((b) => b.status === "new").length,
    read: bookings.filter((b) => b.status === "read").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };

  return (
    <>
      <div className="mb-8">
        <h1 className="font-display text-4xl md:text-5xl mb-2" style={{ color: "var(--color-primary)" }} data-testid="admin-title">Rezervimet</h1>
        <p style={{ color: "var(--color-muted)" }}>{bookings.length} rezervime gjithsej · {counts.new} të reja</p>
      </div>
      <div className="flex gap-2 mb-8 flex-wrap">
        {[{ k: "all", l: "Të gjitha" }, { k: "new", l: "Të reja" }, { k: "read", l: "Të lexuara" }, { k: "confirmed", l: "Të konfirmuara" }, { k: "cancelled", l: "Të anuluara" }].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} className="px-4 py-2 text-xs uppercase tracking-wider transition-colors" style={{ background: filter === f.k ? "var(--color-primary)" : "transparent", color: filter === f.k ? "#fff" : "var(--color-primary)", border: "1px solid var(--color-primary)" }} data-testid={`admin-filter-${f.k}`}>
            {f.l} ({counts[f.k]})
          </button>
        ))}
      </div>
      {error && <div className="mb-6 p-3 text-sm" style={{ background: "#FCE8E8", color: "#7A1F1F" }} data-testid="admin-error">{error}</div>}
      {loading ? (
        <div className="text-center py-20" style={{ color: "var(--color-muted)" }} data-testid="admin-loading">Duke ngarkuar...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed" style={{ borderColor: "var(--color-border)", color: "var(--color-muted)" }} data-testid="admin-empty">
          <div className="font-display text-2xl mb-2" style={{ color: "var(--color-primary)" }}>Asnjë rezervim {filter !== "all" ? "në këtë kategori" : "ende"}</div>
          <p className="text-sm">Rezervimet e reja do të shfaqen këtu automatikisht.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-bookings-list">
          {filtered.map((b) => <BookingCard key={b.id} booking={b} onStatusChange={updateStatus} onDelete={deleteBooking} />)}
        </div>
      )}
    </>
  );
}

function BlockedDatesTab({ onLogout }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: null, to: null });
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchBlocks = async () => {
    try {
      const { data } = await api.get(`${API}/admin/blocked-dates`);
      setBlocks(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBlocks(); }, []);

  const addBlock = async () => {
    if (!range.from || !range.to) {
      setError("Ju lutem zgjidhni datën e fillimit dhe të mbarimit");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`${API}/admin/blocked-dates`, {
        check_in: toYMD(range.from),
        check_out: toYMD(range.to),
        note: note || null,
      });
      setBlocks((prev) => [...prev, data].sort((a, b) => a.check_in.localeCompare(b.check_in)));
      setRange({ from: null, to: null });
      setNote("");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Gabim gjatë shtimit");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBlock = async (id) => {
    if (!window.confirm("Fshi këtë bllokim?")) return;
    try {
      await api.delete(`${API}/admin/blocked-dates/${id}`);
      setBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch { setError("Gabim gjatë fshirjes"); }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <>
      <div className="mb-8">
        <h1 className="font-display text-4xl md:text-5xl mb-2" style={{ color: "var(--color-primary)" }} data-testid="blocked-title">Datat e Bllokuara</h1>
        <p style={{ color: "var(--color-muted)" }}>Blloko data për mirëmbajtje, familje, ose çdo arsye tjetër. Këto datat nuk do të jenë të disponueshme për rezervim online.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="p-6" style={{ background: "#fff", border: "1px solid var(--color-border)" }}>
          <h2 className="font-display text-2xl mb-4" style={{ color: "var(--color-primary)" }}>Blloko Datat e Reja</h2>
          <div className="drp-wrap" style={{ padding: "1rem", border: "1px solid var(--color-border)" }}>
            <DayPicker
              mode="range"
              selected={range}
              onSelect={(r) => setRange(r || { from: null, to: null })}
              disabled={{ before: today }}
              weekStartsOn={1}
              numberOfMonths={1}
            />
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Arsyeja (opsionale): Mirëmbajtje, Familja..."
            className="w-full mt-4 bg-transparent border-0 border-b-2 focus:outline-none py-2 text-sm"
            style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
            data-testid="blocked-note-input"
            maxLength={200}
          />
          {error && <div className="mt-3 p-2 text-sm" style={{ background: "#FCE8E8", color: "#7A1F1F" }} data-testid="blocked-error">{error}</div>}
          <button
            onClick={addBlock}
            disabled={submitting || !range.from || !range.to}
            className="btn-primary w-full justify-center mt-4"
            data-testid="blocked-add-btn"
          >
            {submitting ? "Duke shtuar..." : <><Plus size={16} /> Shto Bllokim</>}
          </button>
          {range.from && range.to && (
            <div className="mt-3 text-xs text-center" style={{ color: "var(--color-muted)" }}>
              {toYMD(range.from)} → {toYMD(range.to)} ({Math.round((range.to - range.from) / 86400000)} net)
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display text-2xl mb-4" style={{ color: "var(--color-primary)" }}>Datat e Bllokuara ({blocks.length})</h2>
          {loading ? (
            <div style={{ color: "var(--color-muted)" }}>Duke ngarkuar...</div>
          ) : blocks.length === 0 ? (
            <div className="p-6 border-2 border-dashed text-center text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-muted)" }} data-testid="blocked-empty">
              Asnjë datë e bllokuar ende. Përdor formularin majtas për të shtuar.
            </div>
          ) : (
            <div className="space-y-3" data-testid="blocked-list">
              {blocks.map((b) => (
                <div key={b.id} className="p-4 flex items-center justify-between gap-4" style={{ background: "#fff", border: "1px solid var(--color-border)" }} data-testid={`blocked-item-${b.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <Prohibit size={18} color="#A35D43" />
                      <span className="font-display text-lg" style={{ color: "var(--color-primary)" }}>
                        {b.check_in} → {b.check_out}
                      </span>
                    </div>
                    {b.note && <div className="text-xs mt-1 ml-7" style={{ color: "var(--color-muted)" }}>{b.note}</div>}
                  </div>
                  <button onClick={() => deleteBlock(b.id)} className="p-2" data-testid={`blocked-delete-${b.id}`}>
                    <Trash size={16} color="#7A1F1F" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("bookings");

  return (
    <div className="min-h-screen" style={{ background: "var(--color-bg)" }} data-testid="admin-dashboard">
      <header className="border-b" style={{ borderColor: "var(--color-border)", background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-display text-2xl" style={{ color: "var(--color-primary)" }}>
              Villa<span style={{ color: "var(--color-secondary)" }}>·</span>Delight
            </div>
            <div className="overline">Admin Panel</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="nav-link" data-testid="admin-view-site">Shiko Site-in</a>
            <button onClick={onLogout} className="btn-secondary" style={{ padding: "0.5rem 1rem", fontSize: "0.7rem" }} data-testid="admin-logout-btn">
              <SignOut size={14} /> Dil
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: "var(--color-border)" }}>
          <button
            onClick={() => setTab("bookings")}
            className="py-4 flex items-center gap-2 text-sm uppercase tracking-wider transition-colors"
            style={{
              color: tab === "bookings" ? "var(--color-primary)" : "var(--color-muted)",
              borderBottom: tab === "bookings" ? "2px solid var(--color-primary)" : "2px solid transparent",
              fontWeight: tab === "bookings" ? 600 : 400,
            }}
            data-testid="tab-bookings"
          >
            <ListChecks size={16} /> Rezervimet
          </button>
          <button
            onClick={() => setTab("blocked")}
            className="py-4 flex items-center gap-2 text-sm uppercase tracking-wider transition-colors"
            style={{
              color: tab === "blocked" ? "var(--color-primary)" : "var(--color-muted)",
              borderBottom: tab === "blocked" ? "2px solid var(--color-primary)" : "2px solid transparent",
              fontWeight: tab === "blocked" ? 600 : 400,
            }}
            data-testid="tab-blocked"
          >
            <Prohibit size={16} /> Datat e Bllokuara
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "bookings" ? <BookingsTab onLogout={onLogout} /> : <BlockedDatesTab onLogout={onLogout} />}
      </main>
    </div>
  );
}

export default function AdminApp() {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuth(false); return; }
    api.get(`${API}/admin/me`)
      .then((res) => setAuth(res.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuth(false); });
  }, []);

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuth(false);
  };

  if (auth === null) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--color-bg)", color: "var(--color-muted)" }}>Duke verifikuar...</div>;
  }
  if (auth === false) return <LoginForm onLogin={setAuth} />;
  return <Dashboard onLogout={onLogout} />;
}
